/**
 * PDCard — поиск банковской карты, приложенной ко лбу, на кадре с лицом.
 *
 * Почему поиск, а не только ручные маркеры. У предшественника (/pd-meter)
 * человек сам тянул две точки на края карты — и за 30 дней там не случилось
 * ни одного завершённого замера (120 сессий, 30 «яростных» кликов). Здесь
 * прибор знает главное заранее: где лоб (лендмарки бровей) и какого размера
 * карта ДОЛЖНА быть при масштабе, который уже дала радужка. Остаётся найти
 * прямоугольник с пропорциями ID-1 (85.60 × 53.98 мм) в полосе над бровями.
 *
 * Как: полоса над бровями пересэмплируется в систему координат, выровненную по
 * межзрачковой оси (голова бывает наклонена), считаются градиенты поперёк
 * каждого из четырёх рёбер, а суммы вдоль рёбер берутся из префиксных сумм —
 * каждая гипотеза (ширина × сдвиг × высота) стоит O(1). Скруглённые углы карты
 * исключены: считается средние 80 % каждого ребра.
 *
 * Ответ — концы ДЛИННОГО ребра (левый и правый край карты посередине её
 * высоты) в пикселях кадра + уверенность. Низкая уверенность — не ошибка:
 * человек ставит точки сам, прибор честно говорит, что карту не нашёл.
 */
(function (root) {
    'use strict';

    var ASPECT = 53.98 / 85.6;

    /** Яркость кадра (ImageData → Float32Array W×H). */
    function luminance(img) {
        var d = img.data, n = img.width * img.height, out = new Float32Array(n);
        for (var i = 0, j = 0; i < n; i++, j += 4) out[i] = 0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2];
        return out;
    }

    function sampler(gray, W, H) {
        return function (x, y) {
            if (x < 0 || y < 0 || x > W - 1.001 || y > H - 1.001) return -1;
            var x0 = x | 0, y0 = y | 0, fx = x - x0, fy = y - y0, i = y0 * W + x0;
            return (gray[i] * (1 - fx) + gray[i + 1] * fx) * (1 - fy) + (gray[i + W] * (1 - fx) + gray[i + W + 1] * fx) * fy;
        };
    }

    /**
     * Одна ориентация: полоса в осях (s вдоль оси глаз, t вверх по лбу).
     * origin — точка между бровями; ux,uy — единичная ось; wExp — ожидаемая
     * ширина карты в пикселях.
     */
    function searchAt(get, origin, ux, uy, wExp, pxPerMm, lim) {
        var nx = uy, ny = -ux;                       // «вверх» по лицу
        var S = Math.round(wExp * 0.95);             // полуширина полосы
        var tMin = Math.round(-8 * pxPerMm);         // чуть ниже линии бровей
        var tMax = Math.round(95 * pxPerMm);         // до верха лба
        if (lim) {
            // Уточнение: полоса только вокруг найденного на грубом проходе.
            S = Math.round(Math.abs(lim.sC) + lim.w * 0.6 + 8);
            tMin = Math.round(lim.tC - lim.w * 0.45 - 8);
            tMax = Math.round(lim.tC + lim.w * 0.45 + 8);
        }
        var cols = 2 * S + 1, rows = tMax - tMin + 1;
        if (cols < 20 || rows < 20 || cols * rows > 2.5e6) return null;

        var A = new Float32Array(cols * rows), valid = 0;
        for (var r = 0; r < rows; r++) {
            var t = tMin + r;
            for (var c = 0; c < cols; c++) {
                var s = c - S;
                var v = get(origin.x + ux * s + nx * t, origin.y + uy * s + ny * t);
                A[r * cols + c] = v; if (v >= 0) valid++;
            }
        }
        if (valid < cols * rows * 0.6) return null;

        // Градиенты поперёк рёбер: вертикальные рёбра (края карты) — по s,
        // горизонтальные (верх/низ) — по t. Префиксные суммы вдоль рёбер.
        var colP = new Float32Array(cols * (rows + 1));  // по t для каждого s
        var rowP = new Float32Array(rows * (cols + 1));  // по s для каждого t
        for (var c2 = 0; c2 < cols; c2++) {
            var acc = 0;
            for (var r2 = 0; r2 < rows; r2++) {
                var g = 0;
                if (c2 > 0 && c2 < cols - 1) {
                    var a = A[r2 * cols + c2 - 1], b = A[r2 * cols + c2 + 1];
                    if (a >= 0 && b >= 0) g = Math.abs(b - a);
                }
                acc += g; colP[c2 * (rows + 1) + r2 + 1] = acc;
            }
        }
        var gSum = 0, gN = 0;
        for (var r3 = 0; r3 < rows; r3++) {
            var acc2 = 0;
            for (var c3 = 0; c3 < cols; c3++) {
                var g2 = 0;
                if (r3 > 0 && r3 < rows - 1) {
                    var a2 = A[(r3 - 1) * cols + c3], b2 = A[(r3 + 1) * cols + c3];
                    if (a2 >= 0 && b2 >= 0) g2 = Math.abs(b2 - a2);
                }
                acc2 += g2; rowP[r3 * (cols + 1) + c3 + 1] = acc2;
                gSum += g2; gN++;
            }
        }
        var gMean = gSum / (gN || 1) + 1e-3;

        function vEdge(c, r0, r1) { var o = c * (rows + 1); return (colP[o + r1] - colP[o + r0]) / Math.max(1, r1 - r0); }
        function hEdge(r, c0, c1) { var o = r * (cols + 1); return (rowP[o + c1] - rowP[o + c0]) / Math.max(1, c1 - c0); }

        var best = null;
        var wLo = Math.max(24, Math.round(wExp * 0.78)), wHi = Math.min(cols - 3, Math.round(wExp * 1.28));
        function consider(w, c0, rb) {
            var h = Math.round(w * ASPECT);
            var c1 = c0 + w, rt = rb + h;
            if (c0 < 1 || c1 > cols - 2 || rb < 1 || rt > rows - 2) return;
            // Карта лежит над переносицей: центр — не дальше трети ширины.
            var off = Math.abs((c0 + c1) / 2 - S);
            if (off > w * 0.3) return;
            var mw = Math.round(w * 0.1), mh = Math.round(h * 0.12);
            var sL = vEdge(c0, rb + mh, rt - mh), sR = vEdge(c1, rb + mh, rt - mh);
            var sB = hEdge(rb, c0 + mw, c1 - mw), sT = hEdge(rt, c0 + mw, c1 - mw);
            // Все четыре ребра обязаны быть; самое слабое решает.
            var weakest = Math.min(sL, sR, Math.max(sB, sT));
            var score = (sL + sR) * 0.35 + (sB + sT) * 0.15 + weakest * 0.6 - off / w * gMean * 0.8;
            if (!best || score > best.score) best = { score: score, c0: c0, c1: c1, rb: rb, rt: rt, w: w, h: h, weakest: weakest };
        }
        if (lim) {
            // Узкое окно вокруг грубого ответа: ширина ±5 %, сдвиги ±tol.
            var wl = Math.floor(lim.w * 0.95), wh = Math.ceil(lim.w * 1.05);
            for (var w1 = wl; w1 <= wh; w1++) {
                var h1 = Math.round(w1 * ASPECT);
                var cBase = Math.round(S + lim.sC - w1 / 2), rBase = Math.round(lim.tC - h1 / 2 - tMin);
                for (var dc1 = -lim.tol; dc1 <= lim.tol; dc1++) for (var dr1 = -lim.tol; dr1 <= lim.tol; dr1++) consider(w1, cBase + dc1, rBase + dr1);
            }
            if (!best) return null;
        } else {
            // Грубо шагом 2 по всем трём осям, затем ±2 вокруг лучшей гипотезы:
            // в восемь раз дешевле полного перебора при том же ответе (рёбра шире
            // двух пикселей по построению — это перепад, а не линия).
            for (var w = wLo; w <= wHi; w += 2) {
                var hh = Math.round(w * ASPECT);
                if (hh + 3 > rows) break;
                for (var c0 = 1; c0 + w < cols - 1; c0 += 2) for (var rb = 1; rb + hh < rows - 1; rb += 2) consider(w, c0, rb);
            }
            if (!best) return null;
            var g0 = best;
            for (var dw = -2; dw <= 2; dw++) for (var dc = -2; dc <= 2; dc++) for (var dr = -2; dr <= 2; dr++) consider(g0.w + dw, g0.c0 + dc, g0.rb + dr);
        }

        // Субпиксель: парабола по профилю вертикального ребра.
        function refine(c) {
            if (c < 2 || c > cols - 3) return c;
            var y0 = vEdge(c - 1, best.rb + mhOf(best), best.rt - mhOf(best));
            var y1 = vEdge(c, best.rb + mhOf(best), best.rt - mhOf(best));
            var y2 = vEdge(c + 1, best.rb + mhOf(best), best.rt - mhOf(best));
            var den = y0 - 2 * y1 + y2;
            return Math.abs(den) < 1e-6 ? c : c + clamp(0.5 * (y0 - y2) / den, -0.5, 0.5);
        }
        function mhOf(b) { return Math.round(b.h * 0.12); }
        var cl = refine(best.c0), cr = refine(best.c1);
        var tMid = tMin + (best.rb + best.rt) / 2;
        function toImg(c, tt) { var s2 = c - S; return { x: origin.x + ux * s2 + nx * tt, y: origin.y + uy * s2 + ny * tt }; }
        var corners = [toImg(best.c0, tMin + best.rb), toImg(best.c1, tMin + best.rb), toImg(best.c1, tMin + best.rt), toImg(best.c0, tMin + best.rt)];
        return {
            left: toImg(cl, tMid), right: toImg(cr, tMid), widthPx: cr - cl,
            sC: (cl + cr) / 2 - S, tC: tMid, hPx: best.h,
            corners: corners, score: best.score,
            // Контраст самого слабого ребра к средней текстуре полосы.
            contrast: best.weakest / gMean,
        };
    }

    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    /**
     * Найти карту. gray — яркость W×H; fm — метрики кадра из PDCore.frameMetrics;
     * pxPerMm — масштаб у глаз (по радужке); opts.depthFactor — поправка на то,
     * что лоб ближе к камере (карта выглядит крупнее).
     */
    /** Уменьшение яркости в k раз усреднением блоков k×k (без наложения спектра). */
    function downsample(gray, W, H, k) {
        var w = Math.floor(W / k), h = Math.floor(H / k), out = new Float32Array(w * h), inv = 1 / (k * k);
        for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
                var acc = 0, y0 = y * k, x0 = x * k;
                for (var dy = 0; dy < k; dy++) { var row = (y0 + dy) * W + x0; for (var dx = 0; dx < k; dx++) acc += gray[row + dx]; }
                out[y * w + x] = acc * inv;
            }
        }
        return { g: out, W: w, H: h };
    }

    /**
     * Найти карту. gray — яркость W×H; fm — метрики кадра из PDCore.frameMetrics
     * (нужны u — ось глаз и brow — точка между бровями); pxPerMm — масштаб у
     * глаз (по радужке); opts.depthFactor — поправка на то, что лоб ближе к
     * камере (карта выглядит крупнее).
     *
     * Два прохода: грубый — на кадре, уменьшенном так, что карта в нём около
     * 150 px (полный перебор стоит миллисекунды), и точный — на полном кадре в
     * окне ±5 % по ширине и несколько пикселей по сдвигу. Полный перебор на
     * кадре 1080p — это миллионы гипотез на кадр, секунды на телефоне.
     */
    function find(gray, W, H, fm, pxPerMm, opts) {
        opts = opts || {};
        if (!fm || !(pxPerMm > 0)) return { ok: false, reason: 'no_face' };
        var wExp = 85.6 * pxPerMm * (opts.depthFactor || 1.03);
        var base = Math.atan2(fm.u.y, fm.u.x);
        var angles = opts.angles || [0, -3, 3, -6, 6];
        var k = Math.max(1, Math.round(wExp / (opts.coarsePx || 150)));
        var minContrast = opts.minContrast || 2.2;

        function run(g, w, h, sc, angs, lims) {
            var get = sampler(g, w, h), best = null;
            var origin = { x: fm.brow.x * sc, y: fm.brow.y * sc };
            for (var i = 0; i < angs.length; i++) {
                var a = base + angs[i] * Math.PI / 180;
                var r = searchAt(get, origin, Math.cos(a), Math.sin(a), wExp * sc, pxPerMm * sc, lims);
                if (r && (!best || r.score > best.score)) { best = r; best.angle = angs[i]; }
            }
            return best;
        }

        var best;
        if (k > 1) {
            var small = downsample(gray, W, H, k);
            var coarse = run(small.g, small.W, small.H, 1 / k, angles, null);
            if (!coarse) return { ok: false, reason: 'no_band' };
            // Контраст решается на грубом проходе: на уменьшенном кадре текстура
            // кожи сглажена, и ложное «ребро» отличается от настоящего сильнее.
            if (coarse.contrast < minContrast * 0.8) {
                return { ok: false, reason: 'weak_edges', contrast: coarse.contrast, coarse: true, expectedPx: wExp };
            }
            var lim = { w: coarse.widthPx * k, sC: coarse.sC * k, tC: coarse.tC * k, tol: k + 1 };
            best = run(gray, W, H, 1, [coarse.angle - 1, coarse.angle, coarse.angle + 1], lim);
            if (!best) return { ok: false, reason: 'no_band' };
            best.coarseContrast = coarse.contrast;
        } else {
            best = run(gray, W, H, 1, angles, null);
            if (!best) return { ok: false, reason: 'no_band' };
        }
        var c = best.coarseContrast != null ? Math.max(best.contrast, best.coarseContrast) : best.contrast;
        best.ok = c >= minContrast;
        best.reason = best.ok ? null : 'weak_edges';
        best.expectedPx = wExp;
        return best;
    }

    var API = { find: find, luminance: luminance, ASPECT: ASPECT };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) root.PDCard = API;
})(typeof window !== 'undefined' ? window : null);
